import { css } from "lit";

/**
 * Component styles. Host pages theme the element through the public
 * `--schulte-roi-*` custom properties; everything else is internal.
 */
export const styles = css`
  :host {
    /* Public theming API with defaults taken from the prototype. */
    --_accent: var(--schulte-roi-accent, #f03241);
    --_accent-hover: var(--schulte-roi-accent-hover, #d81e2d);
    --_accent-soft: var(--schulte-roi-accent-soft, rgba(240, 50, 65, 0.1));
    --_text: var(--schulte-roi-text, #2e3e40);
    --_text-muted: var(--schulte-roi-text-muted, #6b7878);
    --_surface: var(--schulte-roi-surface, #ffffff);
    --_surface-elevated: var(--schulte-roi-surface-elevated, #f0f2f2);
    --_border: var(--schulte-roi-border, #dde2e2);
    --_result-bg: var(--schulte-roi-result-background, linear-gradient(135deg, #2e3e40 0%, #3b4f52 100%));
    --_result-text: var(--schulte-roi-result-text, #ffffff);
    --_success: var(--schulte-roi-success, #2e8a6a);
    --_error: var(--schulte-roi-error, #b3261e);
    --_radius: var(--schulte-roi-radius, 14px);
    --_radius-small: var(--schulte-roi-radius-small, 10px);
    --_shadow: var(--schulte-roi-shadow, 0 8px 30px rgba(46, 62, 64, 0.08));
    --_gap: var(--schulte-roi-gap, 24px);
    --_font: var(--schulte-roi-font, inherit);

    display: block;
    box-sizing: border-box;
    font-family: var(--_font);
    color: var(--_text);
    line-height: 1.6;
    max-width: var(--schulte-roi-max-width, 720px);
    margin: 0 auto;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .card {
    background: var(--_surface);
    border: 1px solid var(--_border);
    border-radius: var(--_radius);
    padding: clamp(18px, 4vw, 32px);
    margin-bottom: var(--_gap);
    box-shadow: var(--_shadow);
  }

  fieldset {
    border: 0;
    padding: 0;
    margin: 0 0 36px;
    min-width: 0;
  }
  fieldset:last-child {
    margin-bottom: 0;
  }

  legend,
  .question-label {
    display: block;
    font-size: 1.05rem;
    font-weight: 600;
    margin-bottom: 18px;
    padding: 0;
  }

  .question-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    background: var(--_accent-soft);
    color: var(--_accent);
    border-radius: 50%;
    font-size: 0.85rem;
    font-weight: 700;
    margin-right: 10px;
  }

  .slider-wrap {
    padding: 8px 4px 0;
  }
  .slider-value {
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    color: var(--_accent);
    margin-bottom: 2px;
    line-height: 1.2;
  }
  .slider-value small {
    display: block;
    font-size: 0.85rem;
    color: var(--_text-muted);
    font-weight: 400;
    margin-top: 4px;
  }
  /* The big number is an input: on phones typing beats dragging a slider across a wide range. */
  .value-input {
    width: 6ch;
    max-width: 100%;
    padding: 0 4px;
    border: 0;
    border-bottom: 2px dashed var(--_border);
    border-radius: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: center;
    -moz-appearance: textfield;
    appearance: textfield;
  }
  .value-input::-webkit-outer-spin-button,
  .value-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .value-input:hover {
    border-bottom-color: var(--_accent);
  }
  .value-input:focus-visible {
    outline: 3px solid var(--_accent-soft);
    outline-offset: 2px;
    border-radius: 6px;
    border-bottom-color: transparent;
  }

  /* 44px tall hit area for touch; the visible track stays thin. */
  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 44px;
    background: transparent;
    outline: none;
    cursor: pointer;
    margin: 0;
  }
  input[type="range"]::-webkit-slider-runnable-track {
    height: 6px;
    background: var(--_surface-elevated);
    border-radius: 3px;
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    box-sizing: border-box;
    width: 28px;
    height: 28px;
    margin-top: -11px;
    background: var(--_accent);
    border-radius: 50%;
    cursor: pointer;
    border: 4px solid var(--_surface);
    box-shadow: 0 0 0 2px var(--_accent);
    transition: transform 0.15s ease;
  }
  input[type="range"]::-webkit-slider-thumb:hover {
    transform: scale(1.12);
  }
  input[type="range"]::-moz-range-track {
    height: 6px;
    background: var(--_surface-elevated);
    border-radius: 3px;
  }
  input[type="range"]::-moz-range-thumb {
    box-sizing: border-box;
    width: 28px;
    height: 28px;
    background: var(--_accent);
    border-radius: 50%;
    cursor: pointer;
    border: 4px solid var(--_surface);
    box-shadow: 0 0 0 2px var(--_accent);
  }
  input[type="range"]:focus-visible {
    outline: 3px solid var(--_accent-soft);
    outline-offset: 2px;
    border-radius: 8px;
  }

  .cap-notice {
    margin-top: 8px;
    padding: 14px 16px;
    border-radius: var(--_radius-small);
    background: var(--_accent-soft);
    border-left: 4px solid var(--_accent);
    font-size: 0.92rem;
    line-height: 1.5;
  }
  .cap-notice strong {
    display: block;
    margin-bottom: 2px;
  }
  .link-btn {
    display: inline-block;
    margin-top: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--_accent);
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .link-btn:focus-visible {
    outline: 3px solid var(--_accent-soft);
    outline-offset: 2px;
    border-radius: 4px;
  }

  .slider-ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 0;
    font-size: 0.8rem;
    color: var(--_text-muted);
  }

  .options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 10px;
  }
  .option-btn {
    background: var(--_surface-elevated);
    border: 1.5px solid var(--_border);
    color: var(--_text);
    padding: 14px 16px;
    border-radius: var(--_radius-small);
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      background 0.18s ease,
      border-color 0.18s ease;
    font-family: inherit;
    line-height: 1.3;
  }
  .option-btn small {
    display: block;
    font-size: 0.78rem;
    font-weight: 400;
    opacity: 0.7;
    margin-top: 2px;
  }
  .option-btn:hover {
    border-color: var(--_accent);
    background: var(--_accent-soft);
  }
  .option-btn[aria-pressed="true"] {
    background: var(--_accent);
    border-color: var(--_accent);
    color: #fff;
  }
  .option-btn:focus-visible {
    outline: 3px solid var(--_accent-soft);
    outline-offset: 2px;
  }

  .result {
    background: var(--_result-bg);
    color: var(--_result-text);
    padding: clamp(18px, 4vw, 32px);
    border-radius: var(--_radius);
    margin-bottom: var(--_gap);
    box-shadow: 0 10px 40px rgba(46, 62, 64, 0.25);
    text-align: center;
  }
  .result-label {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.9;
    margin-bottom: 12px;
  }
  .result-main {
    font-size: clamp(1.3rem, 3.5vw, 1.7rem);
    font-weight: 700;
    margin-bottom: 20px;
    line-height: 1.3;
  }
  .result-main strong {
    font-size: 1.4em;
    display: block;
    margin: 6px 0;
  }
  .result-box {
    background: rgba(255, 255, 255, 0.18);
    padding: 10px 14px;
    border-radius: var(--_radius-small);
    font-size: 0.9rem;
    margin-top: 12px;
  }
  .result-box .amount {
    display: block;
    font-size: 1.15rem;
    font-weight: 700;
    margin-top: 2px;
  }
  .result-box.roi {
    background: linear-gradient(135deg, rgba(46, 138, 106, 0.95), rgba(51, 116, 98, 0.95));
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 16px 20px;
    font-size: 1.05rem;
  }
  .result-box.roi .amount {
    font-size: 2rem;
    font-weight: 800;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.16);
  }
  .result-secondary {
    display: inline-flex;
    margin-top: 8px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 0.92rem;
    font-weight: 700;
  }
  .result-hint {
    display: block;
    margin-top: 8px;
    font-size: 0.72rem;
    font-weight: 400;
    opacity: 0.78;
    line-height: 1.35;
  }
  .result-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    margin-top: 20px;
    font-size: 0.85rem;
  }
  .result-meta-item {
    background: rgba(255, 255, 255, 0.1);
    padding: 10px;
    border-radius: 8px;
  }
  .result-meta-item span {
    display: block;
    opacity: 0.85;
    margin-bottom: 2px;
  }
  .result-meta-item strong {
    font-size: 1rem;
  }
  .result-disclaimer {
    margin-top: 14px;
    font-size: 0.78rem;
    opacity: 0.7;
  }

  .form-section h2 {
    font-size: 1.5rem;
    margin: 0 0 8px;
    text-align: center;
    line-height: 1.3;
  }
  .form-section .subtitle {
    text-align: center;
    color: var(--_text-muted);
    margin: 0 0 28px;
  }
  .form-row {
    margin-bottom: 18px;
  }
  .form-row label {
    display: block;
    font-size: 0.9rem;
    color: var(--_text-muted);
    margin-bottom: 6px;
    font-weight: 500;
  }
  .form-row input {
    width: 100%;
    background: var(--_surface-elevated);
    border: 1.5px solid var(--_border);
    color: var(--_text);
    padding: 14px 16px;
    border-radius: var(--_radius-small);
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.15s;
  }
  .form-row input:focus {
    outline: none;
    border-color: var(--_accent);
  }
  .form-row input:user-invalid {
    border-color: var(--_error);
  }
  .required {
    color: var(--_accent);
    font-weight: 700;
  }
  .optional {
    font-weight: 400;
    opacity: 0.75;
  }
  .required-hint {
    font-size: 0.8rem;
    color: var(--_text-muted);
    margin: -6px 0 18px;
  }

  .consent {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 0.85rem;
    color: var(--_text-muted);
    margin-bottom: 18px;
  }
  .consent input {
    margin-top: 4px;
    accent-color: var(--_accent);
    flex: none;
  }
  .consent a,
  .privacy a {
    color: inherit;
    text-decoration: underline;
  }
  .privacy {
    font-size: 0.8rem;
    color: var(--_text-muted);
    margin: 0 0 18px;
  }

  .submit-btn {
    width: 100%;
    background: var(--_accent);
    color: #fff;
    border: none;
    padding: 16px;
    border-radius: var(--_radius-small);
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;
    margin-top: 8px;
    transition: background 0.15s;
    font-family: inherit;
  }
  .submit-btn:hover:not([disabled]) {
    background: var(--_accent-hover);
  }
  .submit-btn[disabled] {
    opacity: 0.7;
    cursor: progress;
  }
  .submit-btn:focus-visible {
    outline: 3px solid var(--_accent-soft);
    outline-offset: 2px;
  }

  .status {
    padding: 16px;
    border-radius: var(--_radius-small);
    text-align: center;
    margin-top: 16px;
    font-weight: 500;
    color: #fff;
  }
  .status.success {
    background: var(--_success);
  }
  .status.error {
    background: var(--_error);
  }

  .honeypot {
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none !important;
    }
  }
`;
